import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { FaTrashCan } from 'react-icons/fa6';
import { GiBuyCard } from 'react-icons/gi';
import { RiEdit2Fill } from 'react-icons/ri';
import { TbCurrencyDollarCanadian } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import usePackageActions from '../../../hooks/APIHooks/finance/usePackageActions';
import useTransactionActions from '../../../hooks/APIHooks/finance/useTransactionActions';
import { TransactionHistoryReqBody } from '../../../types/history';
import { Package } from '../../../types/packages';
import { Client } from '../../../types/users';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import ConsentModal from '../Modals/ConsentModal';
import CreateEditPackageModal from '../Modals/CreateEditPackageModal';
import UpdateClientProfileModal from '../Modals/UpdateClientProfileModal';

const PackageCard = ({
  creditPackage,
  hasUpdatePermission,
  hasDeletePermission,
}: {
  creditPackage: Package;
  hasUpdatePermission: boolean;
  hasDeletePermission: boolean;
}) => {
  // destructuring the package object here
  const { name, price, credits } = creditPackage || {};

  // destructing the price here
  const [integerPart, fractionPart] = price.toString().split('.');

  // integration of react hooks here
  const [showDeletePackageModal, setShowDeletePackageModal] =
    useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isPurchasing, setIsPurchasing] = useState<boolean>(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false);
  const [showEditPackageModal, setShowEditPackageModal] =
    useState<boolean>(false);
  const [showUpdateClientModal, setShowUpdateClientModal] =
    useState<boolean>(false);

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const { deletePackageByIdHandler } = usePackageActions('');
  const { createNewTransactionHandler } = useTransactionActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // calculating the discounted price here if offer string is available
  // const discountedPrice = useMemo(() => {
  //   if (offer) {
  //     return price - price * 0.2;
  //   }

  //   return -1;
  // }, [offer, price]);

  // destructuring the discounted fraction part here
  // const [disIntegerPart, disFractionPart] = discountedPrice
  //   .toString()
  //   .split('.');

  // handler function to implement purchase package feature declared here
  const purchasePackageHandler = async () => {
    try {
      setIsPurchasing(true);

      const body: TransactionHistoryReqBody = {
        client: currentUser?.id as number,
        transaction_id: 'TRX_KRH1SKR0123HRJ',
        point_package: creditPackage.id,
        credits_purchased: creditPackage.credits,
        cost_amount: creditPackage.price,
        payment_status: 'pending',
        payment_gateway: 'stripe',
        created_by: currentUser?.username,
      };

      console.log('PURCHASE PACKAGE BODY: ', body);

      await createNewTransactionHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Package Purchased!!');

            setShowPurchaseModal(false);

            queryClient.invalidateQueries({
              queryKey: ['current-client-details', currentUser?.id],
            });
          } else {
            toast.error(res.message || 'Package Purchase Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsPurchasing(false);
    }
  };

  // handler function to handle delete package feature declared here
  const deletePackageHandler = async () => {
    try {
      setIsDeleting(true);

      await deletePackageByIdHandler
        .mutateAsync({ packageId: creditPackage.id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Package Deleted!!');

            setShowDeletePackageModal(false);
            queryClient.invalidateQueries({ queryKey: ['package-list'] });
          } else {
            toast.error(res.data.message || 'Package Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering package card component here
  return (
    <>
      <div className='group relative bg-base-white shadow-academia-sm rounded-xl'>
        {/* {offer && <CardRibbon ribbonText={offer} />} */}

        <div className='absolute w-full h-full overflow-hidden rounded-t-xl'>
          <div className='flex items-end justify-center bg-legal-dark-blue shadow-academia-sm min-h-[120px] -skew-y-[8deg] -mt-9'>
            <h3 className='italic text-legal-brown font-medium text-2xl capitalize text-center skew-y-[7deg] pb-6'>
              {name}
            </h3>
          </div>
        </div>

        {currentUserType === 'user' ? (
          <div className='absolute right-5 top-5 opacity-0 group-hover:opacity-100 duration-300'>
            <button
              title={
                !hasDeletePermission
                  ? 'You do not have permissions!'
                  : 'Delete Package'
              }
              onClick={() => setShowDeletePackageModal(true)}
              className='flex items-center justify-center size-8 rounded-full hover:opacity-70 active:scale-95 duration-300 bg-base-red disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100'
              disabled={!hasDeletePermission}
            >
              <FaTrashCan className='size-4 text-base-white' />
            </button>
          </div>
        ) : null}

        <div className='flex flex-col justify-between p-5 h-full min-h-[300px] space-y-5 pt-28'>
          {/* {offer ? (
            <div className='flex items-center justify-center text-gray-300'>
              <div className='relative flex items-center justify-center'>
                <h3 className='font-light text-7xl'>${integerPart}</h3>

                <div>
                  <h4 className='font-light text-4xl'>.{fractionPart}</h4>
                  <p className='font-medium'>/{credits} credits</p>
                </div>

                <div className='absolute w-full h-1 bg-base-red rotate-12 rounded-full'></div>
              </div>
            </div>
          ) : null} */}

          <div className='flex items-center justify-center text-legal-dark-blue flex-1'>
            <h3 className='flex items-center text-7xl'>
              {/* ${+disIntegerPart > 0 ? disIntegerPart : integerPart}  */}
              <TbCurrencyDollarCanadian className='text-[80px]' />
              {integerPart.padStart(2, '0')}
            </h3>
            <div>
              <h4 className='font-light text-4xl'>
                {/* .{disFractionPart ? disFractionPart : fractionPart} */}.
                {fractionPart ? fractionPart.padEnd(2, '0') : '00'}
              </h4>
              <p className='text-base-orange font-medium'>/{credits} credits</p>
            </div>
          </div>

          <div className='space-y-5'>
            {currentUserType === 'client' ? (
              <AcademiaButton
                type='button'
                onClick={() => {
                  if (
                    currentUserType === 'client' &&
                    !(currentUser as Client).is_profile_complete
                  ) {
                    toast.warn('Please, complete your profile first!');
                    setShowUpdateClientModal(true);

                    return;
                  }

                  setShowPurchaseModal(true);
                }}
                variant='primary'
                icon={<GiBuyCard className='size-6' />}
                isLoading={isPurchasing}
                fullWidth
              >
                Purchase
              </AcademiaButton>
            ) : null}

            {currentUserType === 'user' ? (
              <AcademiaButton
                title={
                  !hasUpdatePermission
                    ? 'You do not have permissions!'
                    : undefined
                }
                type='button'
                onClick={() => setShowEditPackageModal(true)}
                variant='info'
                icon={<RiEdit2Fill className='size-6' />}
                isDisabled={!hasUpdatePermission}
                fullWidth
              >
                Edit
              </AcademiaButton>
            ) : null}
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditPackageModal && (
          <CreateEditPackageModal
            title='edit'
            creditPackage={creditPackage}
            handleModalClose={() => setShowEditPackageModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeletePackageModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this (
                <span className='text-base-light-blue font-medium'>
                  {creditPackage.name}
                </span>
                ) package? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deletePackageHandler}
            handleModalClose={() => setShowDeletePackageModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateClientModal && (
          <UpdateClientProfileModal
            client={currentUser as Client}
            handleModalClose={() => setShowUpdateClientModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showPurchaseModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to purchase this (
                <span className='text-base-light-blue font-medium'>
                  {creditPackage.name}
                </span>
                ) package for{' '}
                <span className='text-base-light-blue font-medium'>
                  <TbCurrencyDollarCanadian className='inline size-5' />
                  {creditPackage.price}
                </span>{' '}
                and get{' '}
                <span className='text-base-light-blue font-medium'>
                  {creditPackage.credits}
                </span>{' '}
                credits?
              </>
            }
            isLoading={isPurchasing}
            yesBtnTxt='Yes'
            yesBtnVariant='success'
            noBtnTxt='No'
            noBtnVariant='danger'
            mutationHandler={purchasePackageHandler}
            handleModalClose={() => setShowPurchaseModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PackageCard;
